/**
 * Datenpunkte nach Kanälen gruppiert und in eine Reihenfolge gebracht (HM-049).
 *
 * **Warum überhaupt gruppiert wird.** Ein CTS-Lesekopf mit Zusatzschnittstelle
 * meldet zwei Kanäle mit gleichlautenden Datenpunkten — „Bezug", „Einspeisung",
 * „Wirkleistung" gibt es an jedem. In einer flachen Liste stehen sie
 * abwechselnd untereinander und lassen sich nur am Schlüssel auseinanderhalten;
 * die Frage vor der Seite lautet aber „was misst der zweite Zähler", und die
 * beantwortet ein Abschnitt je Kanal.
 *
 * **Das ist Anzeige, keine zweite Adressform.** Der Datenpunkt hängt am Gerät,
 * der Kanal ist ein Attribut (ADR 0020) — hier wird danach sortiert, nicht
 * dadurch adressiert. Der Schlüssel bleibt flach und opak: Gruppiert wird über
 * `channelId`, **nie** über den Präfix in `ch0.energy-import`. Wer den zerlegt,
 * baut die Kanal-Ebene in den Adresspfad zurück, die HM-044 herausgenommen hat.
 *
 * **Und die Reihenfolge ist gerechnet, nicht gespeichert.** Es gibt kein Feld
 * für sie und es soll keines geben: Eine Sortierzahl je Datenpunkt müsste eine
 * Firmware melden oder jemand pflegen, und beim nächsten Gerätetyp wäre sie
 * wieder leer. Sortiert wird deshalb nach der Größe — Zählerstände, dann
 * Leistung, dann Netzqualität, zuletzt was ohne Einheit ist — und innerhalb
 * einer Größe nach dem Schlüssel.
 */
import type { ApiComponents } from "@hausmeter/sdk";
import { timestampMs } from "./format.js";

type Device = ApiComponents["schemas"]["Device"];
type Datapoint = ApiComponents["schemas"]["Datapoint"];
type DatapointValue = ApiComponents["schemas"]["DatapointValue"];
type Unit = NonNullable<Datapoint["unit"]>;

/** Ein Datenpunkt mit dem, was zuletzt zu ihm eingegangen ist. */
export interface DatapointEntry {
  readonly point: Datapoint;
  /**
   * `null` heißt: noch nie ein Wert. Kein Platzhalter, keine Null (Regel 8).
   *
   * **Bei einem Textdatenpunkt ist `null` der Normalfall und keine Aussage**
   * (HM-052): Er hat keine Messreihe, sein Wert steht in `point.textValue`. Wer
   * hier ein „noch kein Wert" anzeigt, schreibt es an eine Zeile, deren Wert
   * daneben steht.
   */
  readonly value: DatapointValue | null;
  /**
   * Ob dieser Wert deutlich älter ist als der jüngste der Gruppe.
   *
   * **Der Grund, warum nicht an jeder Zeile ein Zeitpunkt steht.** Kommen alle
   * Werte aus derselben Meldung — der Regelfall —, ist ein „vor 12 Sekunden"
   * hinter jeder Zeile fünfzehnmal dieselbe Auskunft. Steht ein einzelner
   * Datenpunkt still, während die anderen laufen, ist genau das die
   * interessante Aussage, und dann bekommt er sein Alter dazu.
   */
  readonly stale: boolean;
}

export interface DatapointGroup {
  /** Die Kanalkennung, `null` für Datenpunkte ohne Kanal. */
  readonly channelId: string | null;
  /**
   * Die Überschrift — `null`, wenn keine nötig ist.
   *
   * Ein Gerät ohne jeden Kanal bekommt **eine** Gruppe ohne Titel: Eine
   * Überschrift „Ohne Kanal" über der einzigen Liste benennt einen Unterschied,
   * den es an diesem Gerät nicht gibt.
   */
  readonly title: string | null;
  /** Der Kanalschlüssel für die Kopfzeile — `ch0`. */
  readonly channelKey: string | null;
  /** Ob der Kanal am Gerät abgeschaltet ist. */
  readonly disabled: boolean;
  /** Der jüngste Messzeitpunkt in dieser Gruppe, `null` ohne jeden Wert. */
  readonly latestAt: string | null;
  readonly entries: readonly DatapointEntry[];
}

/**
 * Ab wann ein Wert innerhalb seiner Gruppe als stehengeblieben gilt.
 *
 * Fünf Minuten, weil das die Untergrenze der Erreichbarkeitsprüfung ist
 * (`@hausmeter/domain/device/presence`): Was darunter liegt, ist der
 * Sendeabstand zweier Nachrichten und keine Auffälligkeit.
 */
const STALE_AFTER_MS = 5 * 60 * 1000;

/**
 * Die Größen in der Reihenfolge, in der jemand sie sucht: erst der
 * Zählerstand, dann die Leistung, dann die Netzqualität, zuletzt das Übrige.
 *
 * **Vollständig über den Vertragstyp** — kommt eine Einheit hinzu, meldet der
 * Typprüfer die fehlende Zeile, statt sie stillschweigend ans Ende zu setzen.
 */
const UNIT_ORDER: Record<Unit, number> = {
  Wh: 0,
  W: 1,
  /* Die Blindleistung steht bei der Wirkleistung: Wer die eine sucht, vergleicht
     sie mit der anderen. */
  var: 2,
  l: 3,
  "l/h": 4,
  V: 5,
  A: 6,
  Hz: 7,
  deg: 8,
  ratio: 9,
  count: 10,
  /* Was ein Ladepunkt über Sitzung und Fahrzeug weiß (HM-165): Geld, Emission,
     Strecke, Dauer — nach den Größen, die ein Zähler misst. */
  EUR: 11,
  "EUR/kWh": 12,
  "g/kWh": 13,
  km: 14,
  s: 15,
  /* Wärme (HM-192): Temperatur und Spreizung hinter den Größen des Zählers. */
  degC: 16,
  K: 17,
};

/** Ohne Einheit — ein Schaltausgang etwa — steht zuletzt. */
const WITHOUT_UNIT = 18;

/**
 * Und ein **Textdatenpunkt** steht darunter (HM-052).
 *
 * Er ist keine Messung, sondern eine Stammangabe des Zählers — Hersteller,
 * Geräte-ID. Zwischen den Messgrößen stünde er wie eine, die gerade nichts
 * liefert; unter ihnen liest man ihn als das, was er ist: die Auskunft, welches
 * Gerät hier eigentlich gelesen wird.
 */
const TEXT_LAST = WITHOUT_UNIT + 1;

/**
 * Zahlen im Schlüssel werden als Zahlen verglichen: `ch10` gehört hinter `ch9`
 * und nicht zwischen `ch1` und `ch2`.
 */
const collator = new Intl.Collator("de-DE", { numeric: true, sensitivity: "base" });

/**
 * **Ohne Uhr.** Ob ein Wert stehengeblieben ist, entscheidet der Vergleich mit
 * dem jüngsten Wert derselben Gruppe und nicht die Zeit seit jetzt: Ist das
 * ganze Gerät seit Stunden still, ist nicht ein einzelner Datenpunkt auffällig,
 * sondern das Gerät — und das sagt die Kopfzeile. Die Funktion ist damit von
 * `Date.now()` unabhängig und in einem Test ohne festgehaltene Zeit prüfbar.
 */
export function groupDatapoints(
  device: Device,
  datapoints: readonly Datapoint[],
  values: readonly DatapointValue[],
): readonly DatapointGroup[] {
  const valueOf = new Map(values.map((value) => [value.datapointId, value]));
  const channels = [...device.channels].sort((a, b) => collator.compare(a.key, b.key));

  /**
   * Ein Kanal ohne Datenpunkte kommt trotzdem vor. Das ist eine Aussage: Das
   * Gerät führt diese Anschlussgruppe und meldet nichts daraus — an einem CTS
   * mit unbestückter Zusatzschnittstelle der Normalfall, und wer den zweiten
   * Zähler sucht, findet hier die Antwort statt eine Leerstelle.
   */
  const groups = channels.map((channel) =>
    build(
      channel.id,
      channel.name,
      channel.key,
      !channel.enabled,
      datapoints.filter((point) => point.channelId === channel.id),
      valueOf,
    ),
  );

  const loose = datapoints.filter((point) => point.channelId === null);

  /**
   * Datenpunkte ohne Kanal stehen **hinter** den Kanälen und tragen nur dann
   * eine Überschrift, wenn es Kanäle gibt, von denen sie sich unterscheiden.
   */
  if (loose.length > 0 || groups.length === 0) {
    groups.push(
      build(
        null,
        channels.length === 0 ? null : "Weitere Werte",
        null,
        false,
        loose,
        valueOf,
      ),
    );
  }

  return groups;
}

function build(
  channelId: string | null,
  title: string | null,
  channelKey: string | null,
  disabled: boolean,
  points: readonly Datapoint[],
  valueOf: ReadonlyMap<string, DatapointValue>,
): DatapointGroup {
  const sorted = [...points].sort(compare);
  const times = sorted
    .map((point) => valueOf.get(point.id)?.measuredAt)
    .filter((at): at is string => at !== undefined)
    .map((at) => ({ at, ms: timestampMs(at) }))
    .filter((entry): entry is { at: string; ms: number } => entry.ms !== null);

  const newest = times.reduce<{ at: string; ms: number } | null>(
    (best, entry) => (best === null || entry.ms > best.ms ? entry : best),
    null,
  );

  return {
    channelId,
    title,
    channelKey,
    disabled,
    latestAt: newest?.at ?? null,
    entries: sorted.map((point) => {
      const value = valueOf.get(point.id) ?? null;
      const ms = value === null ? null : timestampMs(value.measuredAt);
      return {
        point,
        value,
        // Verglichen wird gegen den jüngsten Wert der Gruppe und nicht gegen
        // die Uhr des Browsers: Ist das ganze Gerät seit Stunden still, ist
        // nicht ein einzelner Datenpunkt auffällig, sondern das Gerät — und das
        // sagt die Kopfzeile bereits.
        stale: ms !== null && newest !== null && newest.ms - ms > STALE_AFTER_MS,
      };
    }),
  };
}

/** Erst nach der Größe, dann nach dem Schlüssel. */
function compare(a: Datapoint, b: Datapoint): number {
  const rank = orderOf(a) - orderOf(b);
  return rank !== 0 ? rank : collator.compare(a.key, b.key);
}

function orderOf(point: Datapoint): number {
  if (point.valueType === "string") return TEXT_LAST;
  return point.unit === null ? WITHOUT_UNIT : UNIT_ORDER[point.unit];
}
